import EventEmitter from 'event-emitter';
import Task from './clients/index';
import MD5 from './clients/MD5';
import { isFile } from './utils/utils';
import { EVENT, KV } from './common/Constant';
import log from 'loglevel';

const _fileMap = Symbol('_fileMap');
const FORMATS = { rmvb: 1, mp4: 1, avi: 1, wmv: 1, mkv: 1, flv: 1, mp3: 1, wav: 1, mov: 1, srt: 1 };
const SUBJECT_MAX_LENGTH = 64;
// 【Plane 0】Base Latin（part）、CJK、Symbol、Latin ext、Fullwidth Symbol

/**
 * 这个正则表达式现在支持非常广泛的字符集，但仍排除以下内容：
    Windows/macOS/Linux 系统禁止的字符：\/:*?"<>|
    以空格开头或结尾的文件名
    以点号开头或结尾的文件名
    连续的点号（如 file..txt）
    超过 255 个字符的文件名
 * @param {*} fileName
 * @returns
 */
/* prettier-ignore */
/* eslint no-useless-escape: "off" */
const isValidFileName = fileName => {
  const regex =
    /^(?!.*[\\/:*?"<>|])(?!\s)(?!.*\s$)(?!.*\.$)(?!.*\.\.)[\u2E80-\u9FFF\u2000-\u2BFF\u0080-\u024F\u0300-\u052F\uff00-\uFFEF\uAC00-\uD7FFA-Za-z0-9_!@#$%^&+=,;`~()\[\]{}\.\-]{1,255}$/i;
  return regex.test(fileName);
};

/**
 * SDK接口实现
 *
 * @export
 * @class Interface
 * @author zx
 */
export default class Interface {
  constructor(api, store) {
    EventEmitter(this);
    this._api = api;
    this._store = store;
    this[_fileMap] = new Map();
    this.FORMATS = Object.keys(FORMATS);

    this._tasks = [];
    this._pause = false;
    this.isPause = false;
  }

  /**
   * 废弃（仅作为兼容使用）
   */
  async prepCheck(fileList, fillSuccess) {
    if (!(fileList && fileList.length)) return [{ msg: 'fileList参数格式错误' }];

    const check_list = {};
    const result = [];

    for (let index = 0; index < fileList.length; index++) {
      const file = fileList[index];

      try {
        Task.prototype.check(file);
      } catch (e) {
        result.push({ msg: e.message, index, file, event: EVENT.FILE_CHECK_ERROR });
        continue;
      }

      const name = file.name;
      const size = file.size;
      const lastModified = file.lastModified;
      // const fileExt = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();

      const p = '' + size + lastModified + name;
      if (check_list[p]) {
        result.push({ msg: '存在相同文件', file, index, event: EVENT.FILE_LIST_REPEAT });
        continue;
      }
      check_list[p] = file;
      if (fillSuccess) result.push(null);
    }

    return result;
  }

  /**
   * 批量上传（仅上传，不走队列）
   * @param {Array} fileList
   * @param {function} progressfn
   * @param {function} success
   * @param {function} failure
   */
  async upload(fileList = [], progressfn, success, failure) {
    let isValid = true;
    for (const file of fileList) {
      isValid = isValidFileName(file.name);
      if (!isValid) break;
    }
    if (!isValid) {
      if (failure) {
        return failure('不合法的文件名');
      } else {
        return Promise.reject(new Error('不合法的文件名'));
      }
    }

    const tasks = [];
    for (const file of fileList) {
      const params = {
        file: file
      };
      const task = new Task(this._api);
      try {
        task.init(params);
      } catch (e) {
        failure && failure(e);
        continue;
      }
      task.on('progress', function () {
        progressfn && progressfn(task);
      });
      tasks.push(task);
    }

    for (const task of tasks) {
      task._resume();
      try {
        await new Promise(function (resolve, reject) {
          task.once('done', resolve);
          task.once('error', function (task, error) {
            reject(error);
          });
          task.on('pause', resolve);
        });
        // 被暂停，跳过则
        if (task.status !== 1 && task.status !== 3) continue;
        // 上传成功
        this[_fileMap].set(task.file, { filePath: task.result && task.result.filePath });
        success && success(task);
      } catch (e) {
        failure && failure(task, e);
      }
    }
    // 完成上传
  }

  /**
   * 暂停队列
   */
  pause() {
    log.info('pause()');
    this._setPause(true);
    // 暂停正在进行的上传
    if (this._currentTask) this._currentTask._pause();
    setTimeout(() => this.emit(EVENT.UPLOAD_TASK_PAUSE, {}), 100);
  }

  /**
   * 恢复队列
   */
  resume() {
    log.info('resume()');
    this._setPause(false);
    this._nextTask();
    setTimeout(() => this.emit(EVENT.UPLOAD_TASK_RESUME, {}), 100);
  }

  /**
   * 暂停文件上传
   */
  pauseTask(params) {
    const tasks = [];
    if (Array.isArray(params)) {
      for (const t of params) {
        const { task } = this._findTaskById(t && t.id);
        if (task) tasks.push(task);
      }
    } else {
      const { task } = this._findTaskById(params && params.id);
      if (task) tasks.push(task);
    }
    log.info('pauseTask()', tasks.map(t => t.id).join(','));
    if (!tasks.length) return;

    // 暂停选中的task //
    for (const task of tasks) {
      if (this._currentTask === task) this._currentTask = null;
      task._pause();
    }

    // 没有暂停，则继续进行上传
    if (!this._pause) this._nextTask();
  }

  /**
   * 恢复文件上传
   */
  resumeTask(params) {
    const tasks = [];
    if (Array.isArray(params)) {
      for (const t of params) {
        const { task } = this._findTaskById(t && t.id);
        if (task) tasks.push(task);
      }
    } else {
      const { task } = this._findTaskById(params && params.id);
      if (task) tasks.push(task);
    }
    log.info('resumeTask()', tasks.map(t => t.id).join(','));
    if (!tasks.length) return;

    // 设置上传的task //
    for (const task of tasks) task._setPause(false);

    // 没有暂停，则继续进行上传
    if (!this._pause) this._nextTask();
  }

  /**
   * 添加多个上传任务
   */
  add(params) {
    const { files = [] } = params;
    const oldFileUniqKeys = {};
    for (const t of this._tasks) {
      const uniq = t.fileUniqKey();
      if (uniq) oldFileUniqKeys[uniq] = 1;
    }

    const tasks = [];
    for (const file of files) {
      const param = {
        file: file
      };
      const task = new Task(this._api);
      task.init(param);
      const fileUniqKey = task.fileUniqKey();
      if (oldFileUniqKeys[fileUniqKey]) {
        const err = new Error('存在重复文件：' + file.name);
        err.file = file;
        err.type = EVENT.FILE_LIST_REPEAT;
        throw err;
      }

      if (typeof params.isPause === 'boolean') task._setPause(params.isPause);
      task.on('done', this._onDoneTask.bind(this));
      task.on('error', this._onFailTask.bind(this));
      task.on('progress', this._onProgressTask.bind(this));
      task.on('pause', this._onPauseTask.bind(this));
      task.on('resume', this._onResumeTask.bind(this));
      tasks.push(task);
    }

    this._tasks = this._tasks.concat(tasks);

    // 没有暂停，则继续进行上传
    if (!this._pause) this._nextTask();
  }

  /**
   * 移除一个上传任务
   */
  remove(params) {
    const tasks = [];
    if (Array.isArray(params)) {
      for (const t of params) {
        const { task } = this._findTaskById(t && t.id);
        if (task) tasks.push(task);
      }
    } else {
      const { task } = this._findTaskById(params && params.id);
      if (task) tasks.push(task);
    }
    log.info('remove()', tasks.map(t => t.id).join(','));
    for (const task of tasks) {
      const taskIndex = this._tasks.indexOf(task);
      if (taskIndex >= 0) this._tasks.splice(taskIndex, 1);
      // 如果是，停止正在进行的上传
      if (task) task._destroy();
    }

    // 没有暂停，则继续进行上传
    if (!this._pause) this._nextTask();
  }

  /**
   * 删除所有上传任务
   */
  clean() {
    const curr = this._currentTask;
    this._setPause(true);
    this._tasks = [];
    this._currentTask = null;

    if (curr) curr._pause();
  }

  tasks() {
    return this._tasks.slice();
  }

  currentTask() {
    return this._currentTask;
  }

  /**
   * 删除文件（不建议使用，仅为清理中间文件）
   * @param {object} params {file, file_type}
   * @param {function} success
   * @param {function} failure
   */
  async deleteFile(params, success, failure) {
    let { file, md5, file_type } = params;
    if (!isFile(file)) {
      const d = { msg: 'file参数格式错误', data: {} };
      if (failure) return failure(d);
      else {
        throw d;
      }
    }
    if (!md5) {
      md5 = await new MD5().hash.createMD5(file);
    }

    try {
      await this._api.deleteFile(file.name, md5, file_type || 0);
    } catch (err) {
      if (failure) return failure(err);
      throw err;
    }
    success && success();
  }

  /**
   * 生成点播ID
   * @param {object} params 参数{url}
   * @param {function} success 成功回调
   * @param {function} failure 失败回调
   */
  async createDemand(params, success, failure) {
    const { file, fileName = '', is_safe = '0' } = params;
    const filePath = this[_fileMap].get(file).filePath;

    if (!isFile(file)) {
      const d = { msg: 'file参数格式错误', data: {} };
      if (failure) failure(d);
      else {
        throw d;
      }
      return false;
    }

    if (fileName && !isValidFileName(fileName)) {
      const d = { msg: '文件名称包含不允许的字符', data: {} };
      if (failure) failure(d);
      else {
        throw d;
      }
      return false;
    }
    if (fileName.length > SUBJECT_MAX_LENGTH) {
      const d = { msg: `文件名称长度不能超过${SUBJECT_MAX_LENGTH}个字符`, data: {} };
      if (failure) failure(d);
      else {
        throw d;
      }
      return false;
    }
    if (is_safe != '0' && is_safe != '1') {
      const d = { msg: '加密参数格式错误', data: {} };
      if (failure) failure(d);
      else {
        throw d;
      }
      return false;
    }

    const option = {
      source_url: filePath,
      subject: fileName,
      storage: file.size / 1024,
      is_safe: is_safe
    };
    const attribute_id = this._store.getKV(KV.ATTRIBUTE_ID);
    if (attribute_id != undefined && (attribute_id + '').length > 0) {
      option['attribute_id'] = attribute_id;
    }

    try {
      let result = await this._api.createDemand(option);
      success && success({ recordId: result.record_id });
      return { recordId: result.record_id };
    } catch (error) {
      if (failure) failure(error);
      else {
        throw error;
      }
    }
  }

  /**
   * 关联字幕和点播ID
   * @param {object} params 参数{url}
   * @param {function} success 成功回调
   * @param {function} failure 失败回调
   */
  async createSubtitle(params, success, failure) {
    const { vodId, url, lang, isDefault, remark } = params;

    if (lang && lang.length > 10) {
      const d = { msg: '语言类型不符合规范', data: {} };
      if (failure) return failure(d);
      else {
        throw d;
      }
    }

    if (!(vodId && url)) {
      const d = { msg: '请指定vodId和url', data: {} };
      if (failure) return failure(d);
      else {
        throw d;
      }
    }

    const option = {
      vod_id: vodId,
      source_url: url,
      is_default: isDefault ? 1 : 0
    };
    if (lang) option.lang = lang;
    if (remark) option.remark = remark;

    try {
      let result = await this._api.createSubtitle(option);
      success && success({ recordId: result.record_id });
      return { recordId: result.record_id };
    } catch (error) {
      if (failure) return failure(error);
      else {
        throw error;
      }
    }
  }

  /**
   * 销毁实例
   */
  destroy() {
    const curr = this._currentTask;
    this._tasks = [];
    this._currentTask = null;
    this._api = null;
    this._pause = true;
    this.isPause = true;

    if (curr) curr._destroy();
  }

  _findTaskByFile(file) {
    let index = -1;
    for (const task of this._tasks) {
      index += 1;
      if (task.file === file) return { task, index: index };
    }
    return { task: null, index: -1 };
  }

  _findTaskById(id) {
    let index = -1;
    for (const task of this._tasks) {
      index += 1;
      if (task.id === id) return { task, index: index };
    }
    return { task: null, index: -1 };
  }

  // 完成单个上传
  _onDoneTask(task) {
    // index 是兼容字段
    const index = this._tasks.indexOf(task);
    // 不在任务列表的事件
    if (index < 0) return;
    log.debug('_onDoneTask()');

    const params = {
      name: task.file.name,
      file: task.file,
      id: task.id,
      index: index,
      filePath: task.result && task.result.filePath,
      filetype: task.fileType,
      quickUpload: task.quickUpload,
      status: task.status,
      size: task.size,
      task: task,
      msg: '文件上传成功'
    };
    if (task.quickUpload) {
      params.msg = '文件已在服务器中存在';
      this.emit(EVENT.FILE_UPLOAD_EXISTS, params);
    }
    this[_fileMap].set(params.file, { filePath: params.filePath });
    this.emit(EVENT.FILE_UPLOAD_SUCCESS, params);

    // next task
    this._currentTask = null;
    this._nextTask();
  }

  // 单个上传失败
  _onFailTask(task, error) {
    // index 是兼容字段
    const index = this._tasks.indexOf(task);
    // 不在任务列表的事件
    if (index < 0) return;
    const msg = error.message || '文件上传失败';
    log.error('_onFailTask()', error);

    const params = {
      name: task.file.name,
      file: task.file,
      id: task.id,
      index: index,
      status: task.status,
      task: task,
      size: task.size,
      filetype: task.fileType,
      msg: msg
    };
    this.emit(EVENT.FILE_UPLOAD_FAIL, params);

    // next task
    this._currentTask = null;
    this._nextTask();
  }

  // 上传暂停
  _onPauseTask(task) {
    const index = this._tasks.indexOf(task);
    // 不在任务列表的事件
    if (index < 0) return;

    // next task
    this._currentTask = null;
    this._nextTask();
  }

  // 上传恢复
  _onResumeTask(task) {
    // const index = this._tasks.indexOf(task);
    // 不在任务列表的事件
    // if (index < 0) return;
  }

  // 上传进度
  _onProgressTask(task) {
    // 没有进度就不更新
    if (task.progress <= 0) return;
    // index 是兼容字段
    const index = this._tasks.indexOf(task);
    // 不在任务列表的事件
    if (index < 0) return;

    const params = {
      name: task.file.name,
      file: task.file,
      id: task.id,
      index: index,
      filetype: task.fileType,
      size: task.size,
      task: task,
      status: task.status,
      progress: task.progress
    };
    setTimeout(() => this.emit(EVENT.FILE_UPLOAD_PROGRESS, params));
  }

  // 进行下一个文件上传
  _nextTask() {
    let current = this._currentTask;
    // 失败或已完成或不在任务列表
    if ((current && (current.status === 1 || current.status === 3)) || this._tasks.indexOf(current) < 0) {
      current = null;
      this._currentTask = null;
    }
    // 暂停任务
    if (this._pause) return;
    // 本身就没有任务
    if (!this._tasks.length) return;

    // 继续当前任务
    if (current) {
      log.info('当前任务进行中', current.filename);
      current._resume();
      return;
    }

    this._currentTask = null;
    for (const task of this._tasks) {
      // 仅拿出下个待上传完成的任务
      if (!(task.status === 0 && !task._pausea)) continue;
      this._currentTask = task;
      break;
    }
    if (!this._currentTask) {
      log.debug('没有新的上传任务了');
      this.emit(EVENT.UPLOAD_TASK_DRAIN, {});
      return;
    }

    // 开始上传任务
    log.debug('开始新的上传任务', this._currentTask.filename);
    this._currentTask._resume();
  }

  _setPause(v) {
    this._pause = !!v;
    this.isPause = !!v;
  }
}
